/**
 * useObstructionHandlers - Obstruction operation handlers
 *
 * Provides handler functions for obstruction operations:
 * - Adding vertices to polygon obstructions
 * - Finishing/canceling polygon obstructions
 * - Finishing/canceling circle obstructions
 * - Creating rectangle obstructions
 * - Deleting, copying, moving, rotating obstructions
 */

import { useCallback } from 'react';
import { FREE_FIELD_POLYGON_ID } from './useModuleState';
import type { ObstructionState, ObstructionStateActions, ObstructionSelection } from './useObstructionState';

// Types for external dependencies
export interface ObstructionHandlersDeps {
  // From useObstructionState
  obstructionState: ObstructionState & ObstructionStateActions;

  // From component context
  selectedPolygon: number | null;
  isFreeFieldMode: boolean;

  // Optional: for segment preview during drag
  isDrawingSegment?: boolean;
  calculateModulesForBox?: (
    box: [number, number][],
    applyFillPercentage?: boolean
  ) => Array<{ position: [number, number] }>;
  setPreviewModules?: (modules: Array<{ position: [number, number] }>) => void;
}

export interface ObstructionHandlers {
  // Polygon obstruction drawing
  handleAddObstructionVertex: (vertex: [number, number]) => void;
  handleFinishPolygonObstruction: () => void;
  handleCancelPolygonObstruction: () => void;

  // Circle obstruction drawing
  handleSetCircleCenter: (center: [number, number]) => void;
  handleSetCircleRadius: (radius: number) => void;
  handleFinishCircleObstruction: () => void;
  handleCancelCircleObstruction: () => void;

  // Rectangle obstruction
  handleRectangleCreated: (rectangle: [number, number][]) => void;
  handleDragUpdate: (rect: [number, number][] | null) => void;

  // Obstruction operations
  handleDeleteObstruction: (polygonId: number, obstructionIndex: number) => void;
  handleCopyObstruction: (polygonId: number, obstructionIndex: number) => void;
  handleMoveObstruction: (polygonId: number, obstructionIndex: number, newCoords: [number, number][]) => void;
  handleRotateObstruction: (polygonId: number, obstructionIndex: number, degrees?: number) => void;
}

/**
 * Convert circle to polygon with given center and radius
 */
function circleToPolygon(
  center: [number, number],
  radius: number,
  segments: number = 32
): [number, number][] {
  const vertices: [number, number][] = [];
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLng = 111320 * Math.cos(center[0] * Math.PI / 180);

  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    const dLat = (Math.cos(angle) * radius) / metersPerDegreeLat;
    const dLng = (Math.sin(angle) * radius) / metersPerDegreeLng;
    vertices.push([center[0] + dLat, center[1] + dLng]);
  }

  // Close the polygon
  vertices.push(vertices[0]);
  return vertices;
}

/**
 * Rotate coordinates around a center point
 */
function rotateCoords(
  coords: [number, number][],
  degrees: number
): [number, number][] {
  // Calculate center point
  const centerLat = coords.reduce((sum, [lat]) => sum + lat, 0) / coords.length;
  const centerLng = coords.reduce((sum, [, lng]) => sum + lng, 0) / coords.length;

  // Rotate all points around center
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return coords.map(([lat, lng]) => {
    const deltaLat = lat - centerLat;
    const deltaLng = lng - centerLng;

    const newLat = centerLat + (deltaLat * cos - deltaLng * sin);
    const newLng = centerLng + (deltaLat * sin + deltaLng * cos);

    return [newLat, newLng] as [number, number];
  });
}

/**
 * Create a copy of coordinates with an offset
 */
function offsetCoords(
  coords: [number, number][],
  offsetMeters: number = 0.5
): [number, number][] {
  const latDegPerMeter = 1 / 111000;
  const firstCoord = coords[0];
  const lngDegPerMeter = 1 / (111000 * Math.cos(firstCoord[0] * Math.PI / 180));

  return coords.map(([lat, lng]) => [
    lat + offsetMeters * latDegPerMeter,
    lng + offsetMeters * lngDegPerMeter,
  ] as [number, number]);
}

export function useObstructionHandlers(deps: ObstructionHandlersDeps): ObstructionHandlers {
  const {
    obstructionState,
    selectedPolygon,
    isFreeFieldMode,
    isDrawingSegment = false,
    calculateModulesForBox,
    setPreviewModules,
  } = deps;

  const {
    obstructions,
    obstructionDrawingVertices,
    circleObstructionCenter,
    circleObstructionRadius,
    setObstructions,
    setObstructionDrawingVertices,
    setCircleObstructionCenter,
    setCircleObstructionRadius,
    setIsDrawingObstruction,
    setDragPreviewRect,
    setSelectedObstruction,
  } = obstructionState;

  // Get target polygon ID for storing obstructions
  // - In free-field mode: use FREE_FIELD_POLYGON_ID
  // - With selected polygon: use that polygon's ID
  // - No polygon selected: use FREE_FIELD_POLYGON_ID for global obstructions
  const getTargetPolygonId = useCallback(() => {
    if (isFreeFieldMode) return FREE_FIELD_POLYGON_ID;
    return selectedPolygon !== null ? selectedPolygon : FREE_FIELD_POLYGON_ID;
  }, [isFreeFieldMode, selectedPolygon]);

  // ========== Polygon Obstruction Handlers ==========

  const handleAddObstructionVertex = useCallback((vertex: [number, number]) => {
    setObstructionDrawingVertices(prev => [...prev, vertex]);
  }, [setObstructionDrawingVertices]);

  const handleFinishPolygonObstruction = useCallback(() => {
    if (obstructionDrawingVertices.length < 3) {
      console.warn('Need at least 3 vertices to create a polygon obstruction');
      return;
    }

    const targetPolygonId = getTargetPolygonId();
    if (targetPolygonId === null) {
      console.warn('No polygon selected for obstruction');
      return;
    }

    // Close the polygon
    const closedVertices = [...obstructionDrawingVertices];
    const firstVertex = closedVertices[0];
    const lastVertex = closedVertices[closedVertices.length - 1];
    if (firstVertex[0] !== lastVertex[0] || firstVertex[1] !== lastVertex[1]) {
      closedVertices.push(firstVertex);
    }

    // Add to obstructions
    const polygonObstructions = obstructions[targetPolygonId] || [];
    setObstructions({
      ...obstructions,
      [targetPolygonId]: [...polygonObstructions, closedVertices],
    });

    // Clear drawing state
    setObstructionDrawingVertices([]);
  }, [obstructionDrawingVertices, obstructions, setObstructions, setObstructionDrawingVertices, getTargetPolygonId]);

  const handleCancelPolygonObstruction = useCallback(() => {
    setObstructionDrawingVertices([]);
    setIsDrawingObstruction(false);
  }, [setObstructionDrawingVertices, setIsDrawingObstruction]);

  // ========== Circle Obstruction Handlers ==========

  const handleSetCircleCenter = useCallback((center: [number, number]) => {
    setCircleObstructionCenter(center);
  }, [setCircleObstructionCenter]);

  const handleSetCircleRadius = useCallback((radius: number) => {
    setCircleObstructionRadius(radius);
  }, [setCircleObstructionRadius]);

  const handleFinishCircleObstruction = useCallback(() => {
    if (!circleObstructionCenter || circleObstructionRadius === 0) {
      console.warn('Circle obstruction not fully defined');
      return;
    }

    const targetPolygonId = getTargetPolygonId();
    if (targetPolygonId === null) {
      console.warn('No polygon selected for obstruction');
      return;
    }

    // Convert circle to polygon
    const vertices = circleToPolygon(circleObstructionCenter, circleObstructionRadius);

    // Add to obstructions
    const polygonObstructions = obstructions[targetPolygonId] || [];
    setObstructions({
      ...obstructions,
      [targetPolygonId]: [...polygonObstructions, vertices],
    });

    // Clear drawing state
    setCircleObstructionCenter(null);
    setCircleObstructionRadius(0);
    setIsDrawingObstruction(false);
  }, [
    circleObstructionCenter,
    circleObstructionRadius,
    obstructions,
    setObstructions,
    setCircleObstructionCenter,
    setCircleObstructionRadius,
    setIsDrawingObstruction,
    getTargetPolygonId,
  ]);

  const handleCancelCircleObstruction = useCallback(() => {
    setCircleObstructionCenter(null);
    setCircleObstructionRadius(0);
    setIsDrawingObstruction(false);
  }, [setCircleObstructionCenter, setCircleObstructionRadius, setIsDrawingObstruction]);

  // ========== Rectangle Obstruction Handlers ==========

  const handleRectangleCreated = useCallback((rectangle: [number, number][]) => {
    const targetPolygonId = getTargetPolygonId();
    if (targetPolygonId !== null) {
      const polygonObstructions = obstructions[targetPolygonId] || [];
      setObstructions({
        ...obstructions,
        [targetPolygonId]: [...polygonObstructions, rectangle],
      });
    }
    setDragPreviewRect(null);
  }, [obstructions, setObstructions, setDragPreviewRect, getTargetPolygonId]);

  const handleDragUpdate = useCallback((rect: [number, number][] | null) => {
    setDragPreviewRect(rect);

    // Calculate and show preview modules in real-time while drawing segment
    if (rect && isDrawingSegment && calculateModulesForBox && setPreviewModules) {
      const preview = calculateModulesForBox(rect, true);
      setPreviewModules(preview);
    } else if (setPreviewModules) {
      setPreviewModules([]);
    }
  }, [setDragPreviewRect, isDrawingSegment, calculateModulesForBox, setPreviewModules]);

  // ========== Obstruction Operations ==========

  const handleDeleteObstruction = useCallback((polygonId: number, obstructionIndex: number) => {
    const polygonObstructions = obstructions[polygonId] || [];
    const updated = polygonObstructions.filter((_, idx) => idx !== obstructionIndex);
    setObstructions({
      ...obstructions,
      [polygonId]: updated,
    });
    setSelectedObstruction(null);
  }, [obstructions, setObstructions, setSelectedObstruction]);

  const handleCopyObstruction = useCallback((polygonId: number, obstructionIndex: number) => {
    const polygonObstructions = obstructions[polygonId] || [];
    const obstructionToCopy = polygonObstructions[obstructionIndex];
    if (obstructionToCopy) {
      const offsetCopy = offsetCoords(obstructionToCopy, 0.5);
      setObstructions({
        ...obstructions,
        [polygonId]: [...polygonObstructions, offsetCopy],
      });
    }
  }, [obstructions, setObstructions]);

  const handleMoveObstruction = useCallback((
    polygonId: number,
    obstructionIndex: number,
    newCoords: [number, number][]
  ) => {
    const polygonObstructions = obstructions[polygonId] || [];
    const updated = [...polygonObstructions];
    updated[obstructionIndex] = newCoords;
    setObstructions({
      ...obstructions,
      [polygonId]: updated,
    });
  }, [obstructions, setObstructions]);

  const handleRotateObstruction = useCallback((
    polygonId: number,
    obstructionIndex: number,
    degrees: number = 45
  ) => {
    const polygonObstructions = obstructions[polygonId] || [];
    const obstruction = polygonObstructions[obstructionIndex];
    if (!obstruction) return;

    const rotated = rotateCoords(obstruction, degrees);
    const updated = [...polygonObstructions];
    updated[obstructionIndex] = rotated;
    setObstructions({
      ...obstructions,
      [polygonId]: updated,
    });
  }, [obstructions, setObstructions]);

  return {
    handleAddObstructionVertex,
    handleFinishPolygonObstruction,
    handleCancelPolygonObstruction,
    handleSetCircleCenter,
    handleSetCircleRadius,
    handleFinishCircleObstruction,
    handleCancelCircleObstruction,
    handleRectangleCreated,
    handleDragUpdate,
    handleDeleteObstruction,
    handleCopyObstruction,
    handleMoveObstruction,
    handleRotateObstruction,
  };
}

export default useObstructionHandlers;
